import { Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { api, assertNever, getQueryProps } from 'common';

import { DataSourceDisplayNames } from '../../../../../../DocumentTemplates/Form/types.ts';
import { useFlowDefinitionContext } from '../../../../context.ts';
import { BlockAutocomplete } from '../../ConfigFields/Autocomplete/Autocomplete.tsx';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate.tsx';
import { BlockSelectField } from '../../ConfigFields/SelectField.tsx';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField.ts';

const GenerateDocumentMode = api.FlowDefinition.GenerateDocumentMode;
type GenerateDocumentMode = api.FlowDefinition.GenerateDocumentMode;

export function DocumentTemplateBlockFormFields() {
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { data: documentTemplates } = useQuery({
    ...getQueryProps(api.client.DocumentTemplate, 'findAll', { organizationId }),
  });
  const { value: generateDocumentMode } = useSelectedNodeField<GenerateDocumentMode>(
    'config.generateDocumentMode'
  );
  const { value: documentTemplateId } = useSelectedNodeField<string>('config.documentTemplateId');
  const selectedDocumentTemplateDataSources = Array.from(
    new Set(
      documentTemplates
        ?.find((template) => template.id === documentTemplateId)
        ?.commands.map((command) => command.dataSource as api.DocumentTemplate.DataSource) || []
    )
  );

  return (
    <>
      <BlockSelectField
        label="Generate Document From"
        fieldName="config.generateDocumentMode"
        value={generateDocumentMode}
        options={Object.values(GenerateDocumentMode).map((mode) => ({
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
          label: _.startCase(String(mode)),
          value: mode,
        }))}
      />

      {(() => {
        if (!generateDocumentMode) {
          return null;
        }
        switch (generateDocumentMode) {
          case GenerateDocumentMode.DocumentTemplate:
            return (
              <>
                <BlockAutocomplete
                  fieldName="config.documentTemplateId"
                  label="Document Template"
                  options={documentTemplates?.map((template) => template.id) || []}
                  getOptionLabel={(documentTemplateId) =>
                    documentTemplates?.find((template) => template.id === documentTemplateId)
                      ?.name || 'Search'
                  }
                  helperText="Select a document template to use for this block"
                />
                {Object.values(selectedDocumentTemplateDataSources).map((source) => {
                  if (!Object.values(api.DocumentTemplate.DataSource).includes(source)) {
                    return (
                      <Alert key={source} color="error">
                        Unsupported Data Source: {source}
                      </Alert>
                    );
                  }
                  const dataSourceApiKey =
                    api.DocumentTemplate.DataSourceToDataSourceApiKeys[source];
                  return (
                    <BlockValueTemplateField
                      key={dataSourceApiKey}
                      fieldName={`config.valuesByDocumentTemplateKey.${dataSourceApiKey}`}
                      label={DataSourceDisplayNames[source]}
                      required
                      helperText={`Value to use for the ${DataSourceDisplayNames[source]} Data Source`}
                    />
                  );
                })}
              </>
            );
          case GenerateDocumentMode.Markdown:
            return (
              <BlockValueTemplateFieldArea
                fieldName="config.markdown"
                label="Markdown"
                helperText="The markdown to render in the document"
                placeholder="e.g. {{myMarkdown.data}}"
                required
              />
            );
          case GenerateDocumentMode.CombinePdf:
            return (
              <BlockValueTemplateFieldArea
                fieldName="config.fileReferences"
                label="File References"
                helperText="The file references to include in the PDF"
                placeholder="e.g. {{myFileReference.data}}"
                required
              />
            );
          default:
            assertNever('generateDocumentMode not implemented', generateDocumentMode);
        }
      })()}
    </>
  );
}
